import './style.scss'
import React from 'react'
import { FiCheck, FiX as FiXIcon } from 'react-icons/fi'
import { BackLink, Box, Flex, Text, Card, Button, tokens } from 'src/componentv2'
import { fontBold, fontMedium } from 'src/model/v2/interface/constants'
import { StatusTag } from '../components/StatusTag'
import type { UpgradeRequestDetail } from 'src/provider/API/v2/call-service/Customer/index'

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <Flex justify="space-between" items="center" style={{ padding: '1.2rem 0', borderBottom: `1px solid ${tokens.border}` }}>
            <Text fontSize="1.4" color={tokens.textMuted}>{label}</Text>
            <Text fontSize="1.4" style={fontMedium} color={tokens.textDark}>{value}</Text>
        </Flex>
    )
}

// Real API has no combined "customer name" field — build it from parts.
// Falls back gracefully if either half is missing.
function fullName(request: UpgradeRequestDetail): string {
    return [request.firstName, request.lastName].filter(Boolean).join(' ') || '—'
}

function fallback(value: string | null | undefined): string {
    return value ?? '—'
}

export function AccountUpgradeDetail({
                                         request, onBack, onDecision,
                                     }: {
    request: UpgradeRequestDetail
    onBack: () => void
    // The real API identifies a request by (accountNumber, requestedAt), not a
    // single `id` like the old mock did — see PATCH admin/Customers/UpgradeDecision.
    // `reason` is optional and mainly meant for rejections.
    onDecision: (accountNumber: string, requestedAt: string, isApproved: boolean, reason?: string) => void
}) {
    const isPending = request.status === 'Submitted'
        || request.status === 'OngoingAmlScreening'
        || request.status === 'AmlScreeningPassed'
        || request.status === 'IndemnityAccepted'

    const handleDecision = (isApproved: boolean) => {
        if (!request.accountNumber || !request.requestedAt) return
        onDecision(request.accountNumber, request.requestedAt, isApproved)
    }

    return (
        <div className="account-upgrade-detail" style={{ padding: '2.4rem 3.2rem' }}>
            <Flex justify="space-between" items="center">
                <Flex items="center" gap="1.2">
                    <BackLink onClick={onBack} color="#000000">{''}</BackLink>
                    <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>Upgrade Request Details</Text>
                </Flex>

                {isPending && (
                    <Flex gap="0.8">
                        <Button variant="outline" onClick={() => handleDecision(false)}>
                            <FiXIcon size={14} style={{ marginRight: '0.6rem' }} />
                            Reject
                        </Button>
                        <Button onClick={() => handleDecision(true)}>
                            <FiCheck size={14} style={{ marginRight: '0.6rem' }} />
                            Approve
                        </Button>
                    </Flex>
                )}
            </Flex>

            {/* Card doesn't accept a raw `style` prop — overflow clipping is
                handled by `.account-upgrade-detail__summary-card` in
                style.scss instead of `style={{ overflow: 'hidden' }}`. */}
            <Card
                padding="0" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard} topMargin="2.4"
                className="account-upgrade-detail__summary-card"
            >
                <Flex
                    justify="space-between" items="center"
                    style={{ padding: '2rem 2.4rem', borderBottom: `1px solid ${tokens.border}` }}
                >
                    <Flex direction="column" gap="0.4" items="flex-start">
                        <Text fontSize="2" style={fontBold} color={tokens.textDark}>{fullName(request)}</Text>
                        <Text fontSize="1.4" color={tokens.textMuted}>{fallback(request.customerID)} &middot; {fallback(request.accountNumber)}</Text>
                    </Flex>
                    <StatusTag status={request.status ?? 'Unknown'} />
                </Flex>

                <Box style={{ padding: '0 2.4rem' }}>
                    <InfoRow label="Username" value={fallback(request.username)} />
                    <InfoRow label="Customer ID" value={fallback(request.customerID)} />
                    <InfoRow label="Account Number" value={fallback(request.accountNumber)} />
                    <InfoRow label="First Name" value={fallback(request.firstName)} />
                    <InfoRow label="Last Name" value={fallback(request.lastName)} />
                    <InfoRow label="Gender" value={fallback(request.gender)} />
                    <InfoRow label="BVN" value={fallback(request.bvn)} />
                    <InfoRow label="NIN" value={fallback(request.nin)} />
                    <InfoRow label="ID Type" value={fallback(request.idType)} />
                    <InfoRow label="ID Number" value={fallback(request.idNumber)} />
                    <InfoRow label="Phone" value={fallback(request.phoneNumber)} />
                    <InfoRow label="Email" value={fallback(request.emailAddress)} />
                    <InfoRow label="Address" value={fallback(request.address)} />
                    <InfoRow label="Bill Type" value={fallback(request.billType)} />
                    <InfoRow label="Account Locked" value={request.isLocked ? 'Yes' : 'No'} />
                    <InfoRow label="Onboarding Completed" value={request.isOnboardingCompleted ? 'Yes' : 'No'} />
                    <InfoRow label="Last Login" value={fallback(request.lastLoginDate)} />
                    <InfoRow label="Requested At" value={fallback(request.requestedAt)} />
                    <InfoRow label="Upgraded At" value={fallback(request.upgradedAt)} />
                    {request.statusMessage && (
                        <InfoRow label="Status Message" value={request.statusMessage} />
                    )}
                </Box>
            </Card>
        </div>
    )
}

export default AccountUpgradeDetail